import React, { useState, useEffect, useContext } from 'react';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { AuthContext } from '../App';
import axios from 'axios';
import { 
  Trophy, Star, Zap, Target, Award, Medal, Crown,
  Flame, BookOpen, Mic, MessageSquare, Clock, Shield,
  Lock, CheckCircle, TrendingUp, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CabinCrewAchievements() {
  const { user } = useContext(AuthContext);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/cabin-crew/progress`);
      setProgress(response.data);
    } catch (error) {
      console.error('Progress fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Başarı tanımları
  const achievements = [
    // Mil Başarıları
    {
      id: 'xp_100',
      category: 'xp',
      icon: Star,
      title: 'İlk Adım',
      description: '100 Mil kazanın',
      requirement: 100,
      color: 'from-amber-400 to-yellow-500',
      rarity: 'common'
    },
    {
      id: 'xp_500',
      category: 'xp',
      icon: Zap,
      title: 'Yükselen Yıldız',
      description: '500 Mil kazanın',
      requirement: 500,
      color: 'from-blue-400 to-cyan-500',
      rarity: 'uncommon'
    },
    {
      id: 'xp_1000',
      category: 'xp',
      icon: Trophy,
      title: 'Mil Ustası',
      description: '1000 Mil kazanın',
      requirement: 1000,
      color: 'from-purple-400 to-pink-500',
      rarity: 'rare'
    },
    {
      id: 'xp_2500',
      category: 'xp',
      icon: Crown,
      title: 'Mil Kralı',
      description: '2500 Mil kazanın',
      requirement: 2500,
      color: 'from-amber-500 to-orange-600',
      rarity: 'epic'
    },
    {
      id: 'xp_5000',
      category: 'xp',
      icon: Sparkles,
      title: 'Efsane',
      description: '5000 Mil kazanın',
      requirement: 5000,
      color: 'from-rose-500 to-red-600',
      rarity: 'legendary'
    },
    // Ders Başarıları
    {
      id: 'lessons_5',
      category: 'lessons',
      icon: BookOpen,
      title: 'Öğrenci',
      description: '5 ders tamamlayın',
      requirement: 5,
      color: 'from-green-400 to-emerald-500',
      rarity: 'common'
    },
    {
      id: 'lessons_20',
      category: 'lessons',
      icon: BookOpen,
      title: 'Çalışkan',
      description: '20 ders tamamlayın',
      requirement: 20,
      color: 'from-teal-400 to-cyan-500',
      rarity: 'uncommon'
    },
    {
      id: 'lessons_50',
      category: 'lessons',
      icon: BookOpen,
      title: 'Akademisyen',
      description: '50 ders tamamlayın',
      requirement: 50,
      color: 'from-indigo-400 to-violet-500',
      rarity: 'rare'
    },
    // Seri Başarıları
    {
      id: 'streak_3',
      category: 'streak',
      icon: Flame,
      title: 'Ateşli Başlangıç',
      description: '3 gün üst üste çalışın',
      requirement: 3,
      color: 'from-orange-400 to-red-500',
      rarity: 'common'
    },
    {
      id: 'streak_7',
      category: 'streak',
      icon: Flame,
      title: 'Haftalık Şampiyon',
      description: '7 gün üst üste çalışın',
      requirement: 7,
      color: 'from-red-400 to-rose-500',
      rarity: 'uncommon'
    },
    {
      id: 'streak_30',
      category: 'streak',
      icon: Flame,
      title: 'Aylık Efsane',
      description: '30 gün üst üste çalışın',
      requirement: 30,
      color: 'from-pink-500 to-red-600',
      rarity: 'epic'
    },
    // Modül Başarıları
    {
      id: 'module_a1a2',
      category: 'level',
      icon: Target,
      title: 'Temel Tamamlandı',
      description: 'A1-A2 modüllerini bitirin',
      requirement: 'A1-A2',
      color: 'from-emerald-400 to-green-500',
      rarity: 'uncommon'
    },
    {
      id: 'module_b1b2',
      category: 'level',
      icon: Target,
      title: 'Orta Seviye Ustası',
      description: 'B1-B2 modüllerini bitirin',
      requirement: 'B1-B2',
      color: 'from-blue-400 to-indigo-500',
      rarity: 'rare'
    },
    {
      id: 'module_c1c2',
      category: 'level',
      icon: Target,
      title: 'İleri Seviye Uzmanı',
      description: 'C1-C2 modüllerini bitirin',
      requirement: 'C1-C2',
      color: 'from-purple-500 to-violet-600',
      rarity: 'epic'
    },
    // Özel Başarılar
    {
      id: 'interview_master',
      category: 'special',
      icon: MessageSquare,
      title: 'Mülakat Uzmanı',
      description: '10 mülakat simülasyonu tamamlayın',
      requirement: 10,
      color: 'from-sky-400 to-blue-500',
      rarity: 'rare'
    },
    {
      id: 'voice_pro',
      category: 'special',
      icon: Mic,
      title: 'Ses Profesyoneli',
      description: '20 sesli pratik yapın',
      requirement: 20,
      color: 'from-violet-400 to-purple-500',
      rarity: 'rare'
    },
    {
      id: 'emergency_expert',
      category: 'special',
      icon: Shield,
      title: 'Acil Durum Uzmanı',
      description: 'Tüm acil durum senaryolarını tamamlayın',
      requirement: 'all',
      color: 'from-red-500 to-rose-600',
      rarity: 'epic'
    },
    {
      id: 'speed_demon',
      category: 'special',
      icon: Clock,
      title: 'Hız Şeytanı',
      description: 'Zamanlı yarışmada %90+ skor alın',
      requirement: 90,
      color: 'from-amber-500 to-orange-600',
      rarity: 'legendary'
    }
  ];

  const getProgressValue = (achievement) => {
    if (!progress) return 0;
    
    switch (achievement.category) {
      case 'xp':
        return Math.min(100, (progress.total_xp / achievement.requirement) * 100);
      case 'lessons':
        return Math.min(100, (progress.completed_lessons / achievement.requirement) * 100);
      case 'streak':
        return Math.min(100, ((progress.streak || 0) / achievement.requirement) * 100);
      default:
        return 0;
    }
  };

  const isUnlocked = (achievement) => {
    if (!progress) return false;
    
    switch (achievement.category) {
      case 'xp':
        return progress.total_xp >= achievement.requirement;
      case 'lessons':
        return progress.completed_lessons >= achievement.requirement;
      case 'streak':
        return (progress.streak || 0) >= achievement.requirement;
      default:
        return false;
    }
  };

  const rarityColors = {
    common: 'bg-gray-100 text-gray-600 border-gray-300',
    uncommon: 'bg-green-100 text-green-600 border-green-300',
    rare: 'bg-blue-100 text-blue-600 border-blue-300',
    epic: 'bg-purple-100 text-purple-600 border-purple-300',
    legendary: 'bg-amber-100 text-amber-600 border-amber-300'
  };

  const rarityLabels = {
    common: 'Yaygın',
    uncommon: 'Nadir',
    rare: 'Rare',
    epic: 'Epic',
    legendary: 'Efsanevi'
  };

  const categories = [
    { id: 'all', label: 'Tümü', icon: Trophy },
    { id: 'miles', label: 'Mil', icon: Star },
    { id: 'lessons', label: 'Dersler', icon: BookOpen },
    { id: 'streak', label: 'Seri', icon: Flame },
    { id: 'level', label: 'Seviye', icon: Target },
    { id: 'special', label: 'Özel', icon: Award }
  ];

  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredAchievements = selectedCategory === 'all' 
    ? achievements 
    : achievements.filter(a => a.category === selectedCategory);

  const unlockedCount = achievements.filter(a => isUnlocked(a)).length;
  const totalCount = achievements.length;

  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
        </div>
      </CabinCrewLayout>
    );
  }

  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 p-4">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-800 flex items-center gap-3">
              <Trophy className="w-8 h-8 text-amber-500" />
              Başarılar
            </h1>
            <p className="text-gray-600">Eğitim yolculuğunuzdaki başarılarınız</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
              <CardContent className="p-4 text-center">
                <Trophy className="w-8 h-8 mx-auto mb-2" />
                <p className="text-3xl font-bold">{unlockedCount}/{totalCount}</p>
                <p className="text-sm opacity-80">Başarı Kazanıldı</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
              <CardContent className="p-4 text-center">
                <Star className="w-8 h-8 mx-auto mb-2" />
                <p className="text-3xl font-bold">{progress?.total_xp || 0}</p>
                <p className="text-sm opacity-80">Toplam Mil</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
              <CardContent className="p-4 text-center">
                <BookOpen className="w-8 h-8 mx-auto mb-2" />
                <p className="text-3xl font-bold">{progress?.completed_lessons || 0}</p>
                <p className="text-sm opacity-80">Tamamlanan Ders</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-red-500 to-rose-500 text-white">
              <CardContent className="p-4 text-center">
                <Flame className="w-8 h-8 mx-auto mb-2" />
                <p className="text-3xl font-bold">{progress?.streak || 0}</p>
                <p className="text-sm opacity-80">Günlük Seri</p>
              </CardContent>
            </Card>
          </div>

          {/* Progress Bar */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Genel İlerleme</span>
                <span className="text-sm text-gray-500">{Math.round((unlockedCount / totalCount) * 100)}%</span>
              </div>
              <Progress value={(unlockedCount / totalCount) * 100} className="h-3" />
            </CardContent>
          </Card>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className={selectedCategory === cat.id ? 'bg-amber-500' : ''}
              >
                <cat.icon className="w-4 h-4 mr-1" />
                {cat.label}
              </Button>
            ))}
          </div>

          {/* Achievements Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAchievements.map(achievement => {
              const unlocked = isUnlocked(achievement);
              const progressValue = getProgressValue(achievement);
              
              return (
                <Card 
                  key={achievement.id} 
                  className={`relative overflow-hidden transition-all duration-300 ${
                    unlocked 
                      ? 'border-2 border-amber-300 shadow-lg' 
                      : 'opacity-75 grayscale hover:grayscale-0 hover:opacity-100'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${achievement.color} flex items-center justify-center shadow-lg ${
                        !unlocked && 'opacity-50'
                      }`}>
                        {unlocked ? (
                          <achievement.icon className="w-7 h-7 text-white" />
                        ) : (
                          <Lock className="w-7 h-7 text-white/70" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold">{achievement.title}</h3>
                          {unlocked && (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{achievement.description}</p>
                        <Badge className={rarityColors[achievement.rarity]}>
                          {rarityLabels[achievement.rarity]}
                        </Badge>
                      </div>
                    </div>
                    
                    {!unlocked && achievement.category !== 'level' && achievement.category !== 'special' && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>İlerleme</span>
                          <span>{Math.round(progressValue)}%</span>
                        </div>
                        <Progress value={progressValue} className="h-2" />
                      </div>
                    )}

                    {unlocked && (
                      <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                        <div className="absolute transform rotate-45 bg-amber-500 text-white text-xs py-1 right-[-35px] top-[15px] w-[120px] text-center shadow">
                          ✓
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Motivation */}
          <Card className="mt-8 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
            <CardContent className="p-6 text-center">
              <TrendingUp className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Devam Et!</h3>
              <p className="text-white/80">
                Şu ana kadar {unlockedCount} başarı kazandın. 
                {totalCount - unlockedCount} başarı daha seni bekliyor!
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </CabinCrewLayout>
  );
}
